import { AnswerModel } from '@/components/decision-tree/hooks';
import { getMaskedNumericValue } from '@/utils';
import { ChangeEvent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Measurement } from '../Height';
import { WeightAnswer, WeightValidation } from './types';

type ErrorType = { value: number; message: string };

type WeightControllerProps = {
  id: string;
  validationValues?: WeightValidation;
  saveAnswer: (payload: AnswerModel) => void;
  defaultValue?: WeightAnswer;
  pounds: Measurement;
  ounces: Measurement;
};

export default function useWeightController({
  id,
  validationValues,
  saveAnswer,
  defaultValue,
  pounds,
  ounces,
}: WeightControllerProps) {
  const form = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { pounds: defaultValue?.pounds ?? '', ounces: defaultValue?.ounces ?? '' },
  });

  const poundsValidationOptions: {
    min?: ErrorType;
    max?: ErrorType;
  } = {};
  if (validationValues?.pounds.min)
    poundsValidationOptions.min = {
      value: validationValues.pounds.min,
      message: 'The value you entered seems unusually low',
    };
  if (validationValues?.pounds.max)
    poundsValidationOptions.max = {
      value: validationValues.pounds.max,
      message: 'The value you entered seems unusually high',
    };

  const ouncesValidationOptions: {
    min?: ErrorType;
    max?: ErrorType;
  } = {};
  if (validationValues?.ounces.min)
    ouncesValidationOptions.min = {
      value: validationValues.ounces.min,
      message: 'The value you entered seems unusually low',
    };
  if (validationValues?.ounces.max)
    ouncesValidationOptions.max = {
      value: validationValues.ounces.max,
      message: 'The value you entered seems unusually high',
    };

  const [answers, setAnswers] = useState<WeightAnswer>({ type: 'weight', pounds: null, ounces: null });
  useEffect(() => setAnswers({ type: 'weight', pounds: null, ounces: null }), [id]);

  const handleChange = form.handleSubmit(({ pounds, ounces }) => {
    const newAnswer = { ...answers, pounds: Number(pounds), ounces: Number(ounces) };
    setAnswers(newAnswer);
    saveAnswer(newAnswer);
  });

  const onInputPoundsFn = ({ currentTarget }: ChangeEvent<HTMLInputElement>) => {
    form.setValue('pounds', getMaskedNumericValue(currentTarget.value, pounds.trailingMask ?? ''));
    if (form.formState.errors.pounds) handleChange();
  };

  const onInputOuncesFn = ({ currentTarget }: ChangeEvent<HTMLInputElement>) => {
    form.setValue('ounces', getMaskedNumericValue(currentTarget.value, ounces.trailingMask ?? ''));
    if (form.formState.errors.ounces) handleChange();
  };

  const resetSavedAnswer = () => {
    setAnswers({ type: 'weight', pounds: null, ounces: null });
    saveAnswer({ type: 'weight', pounds: null, ounces: null });
  };

  const onBlurPoundsFn = ({ currentTarget }: ChangeEvent<HTMLInputElement>) => {
    resetSavedAnswer();
    form.setValue('pounds', getMaskedNumericValue(currentTarget.value, pounds.trailingMask ?? ''));
    handleChange();
  };

  const onBlurOuncesFn = ({ currentTarget }: ChangeEvent<HTMLInputElement>) => {
    resetSavedAnswer();
    form.setValue('ounces', getMaskedNumericValue(currentTarget.value, ounces.trailingMask ?? ''));
    handleChange();
  };

  return {
    onInputPoundsFn,
    onInputOuncesFn,
    form,
    poundsValidationOptions,
    ouncesValidationOptions,
    onBlurPoundsFn,
    onBlurOuncesFn,
  };
}
